import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react'
import { useUser } from '@fs/zion-user'
import { useLazyRequest } from '../hooks/useRequest'
import {
  getSubscriptions,
  registerForARC,
  registerForDiscoveryDay,
  easyRegister,
  registerForExpoHall,
  register,
  subscribe,
  updateSubscriptions,
} from '../api/rootstech/registration'

const initialState = {
  hasSmsSubscription: false,
  hasOnlineRegistration: false,
  hasInPersonRegistration: false,
  hasOnlineSubscription: false,
  hasInPersonSubscription: false,
  hasArcRegistration: false,
  hasArcSubscription: false,
  hasExpoHallRegistration: false,
  hasDiscoveryDayRegistration: false,
}

const SUBSCRIPTION_NAME_MAP = {
  'RootsTech for SMS/MMS': 'hasSmsSubscription',
  'Online RootsTech Event': 'hasOnlineRegistration',
  'Online RootsTech Marketing': 'hasOnlineSubscription',
  'In-Person RootsTech Event': 'hasInPersonRegistration',
  'In-Person RootsTech Marketing': 'hasInPersonSubscription',
  'ARC Symposium Event': 'hasArcRegistration',
  'ARC Symposium Marketing': 'hasArcSubscription',
  'Free Expo Hall': 'hasExpoHallRegistration',
  'Free Family Day': 'hasDiscoveryDayRegistration',
}

const mapSubscriptionsFromAPI = (data) => {
  if (!data) {
    return initialState
  }

  const subscriptionGroups = data?.brazeUserSubscriptionResponse?.users?.[0]?.subscription_groups || []

  // Create lookup map for O(n) complexity
  const subscriptionLookup = subscriptionGroups.reduce((acc, group) => {
    const mappedName = SUBSCRIPTION_NAME_MAP[group.name]
    if (mappedName) {
      acc[mappedName] = group.status === 'Subscribed'
    }
    return acc
  }, {})

  return {
    ...initialState,
    ...subscriptionLookup,
  }
}
const computeDerivedProperties = (subscriptions) => {
  const hasAnyInPersonRegistration =
    subscriptions.hasInPersonRegistration ||
    subscriptions.hasExpoHallRegistration ||
    subscriptions.hasDiscoveryDayRegistration

  const hasAnyRegistration =
    subscriptions.hasOnlineRegistration ||
    subscriptions.hasInPersonRegistration ||
    subscriptions.hasArcRegistration ||
    subscriptions.hasDiscoveryDayRegistration ||
    subscriptions.hasExpoHallRegistration

  const hasAnySubscription =
    subscriptions.hasOnlineSubscription ||
    subscriptions.hasInPersonSubscription ||
    subscriptions.hasArcSubscription ||
    subscriptions.hasSmsSubscription

  const hasBothMainRegistrations = subscriptions.hasOnlineRegistration && subscriptions.hasInPersonRegistration
  const hasOnlyOnlineRegistration = subscriptions.hasOnlineRegistration && !subscriptions.hasInPersonRegistration
  const hasOnlyInPersonRegistration = !subscriptions.hasOnlineRegistration && subscriptions.hasInPersonRegistration

  return {
    hasAnyInPersonRegistration,
    hasAnyRegistration,
    hasAnySubscription,
    hasBothMainRegistrations,
    hasOnlyOnlineRegistration,
    hasOnlyInPersonRegistration,
  }
}

const useSubscriptionData = (isSignedIn) => {
  const [subscriptions, setSubscriptions] = useState(initialState)
  const [shouldRefetch, setShouldRefetch] = useState(true)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    let isMounted = true

    async function fetchSubscriptions() {
      if (!isSignedIn || !shouldRefetch) {
        return
      }

      setLoading(true)
      try {
        const response = await getSubscriptions()
        if (isMounted && !response.error) {
          setSubscriptions(mapSubscriptionsFromAPI(response.data))
          setShouldRefetch(false)
        }
      } catch (error) {
        console.error('Failed to fetch subscriptions:', error)
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    fetchSubscriptions()

    return () => {
      isMounted = false
    }
  }, [isSignedIn, shouldRefetch])

  const refetch = useCallback(() => {
    if (isSignedIn) {
      setShouldRefetch(true)
    }
  }, [isSignedIn])

  return { subscriptions, loading, refetch }
}

/**
 * Creates a registration handler that automatically refreshes subscriptions on success
 * @param {Function} registrationFn - Registration function from useLazyRequest
 * @param {Function} onSuccess - Callback to run on successful registration
 * @returns {Function} Wrapped registration function
 */
const useAutoRefreshingSubscriptions = (registrationFn, onSuccess) => {
  return useCallback(
    async (body) => {
      const response = await registrationFn(body)
      if (response && !response.error) {
        onSuccess()
      }
      return response
    },
    [registrationFn, onSuccess]
  )
}

const RegistrationContext = createContext(null)

export const useRegistrationContext = () => {
  const context = useContext(RegistrationContext)
  if (!context) {
    throw new Error('useRegistrationContext must be used within RegistrationProvider')
  }
  return context
}

/**
 * Provides registration state and actions throughout the app
 * Automatically manages subscription data fetching and refresh
 */
export function RegistrationProvider({ children }) {
  const { signedIn } = useUser()
  const { subscriptions, loading, refetch } = useSubscriptionData(signedIn)

  // Lazy request hooks for each registration type
  const [sendOnlineRegistration] = useLazyRequest(register)
  const [sendExpoHallRegistration] = useLazyRequest(registerForExpoHall)
  const [sendARCRegistration] = useLazyRequest(registerForARC)
  const [sendDiscoveryDayRegistration] = useLazyRequest(registerForDiscoveryDay)
  const [sendEasyRegistration] = useLazyRequest(easyRegister)
  const [sendSubscription] = useLazyRequest(subscribe)
  const [sendSubscriptionUpdates] = useLazyRequest(updateSubscriptions)

  // Auto-refreshing registration handlers
  const registerForOnlineEvent = useAutoRefreshingSubscriptions(sendOnlineRegistration, refetch)
  const registerForExpoHallEvent = useAutoRefreshingSubscriptions(sendExpoHallRegistration, refetch)
  const registerForARCEvent = useAutoRefreshingSubscriptions(sendARCRegistration, refetch)
  const registerForDiscoveryDayEvent = useAutoRefreshingSubscriptions(sendDiscoveryDayRegistration, refetch)
  const registerWithEasyRegistration = useAutoRefreshingSubscriptions(sendEasyRegistration, refetch)
  const subscribeToMarketing = useAutoRefreshingSubscriptions(sendSubscription, refetch)
  const updateMarketingPreferences = useAutoRefreshingSubscriptions(sendSubscriptionUpdates, refetch)

  const derivedProperties = useMemo(() => computeDerivedProperties(subscriptions), [subscriptions])

  const value = useMemo(
    () => ({
      // Registration actions
      registerForOnlineEvent,
      registerForExpoHallEvent,
      registerForARCEvent,
      registerForDiscoveryDayEvent,
      registerWithEasyRegistration,
      subscribeToMarketing,
      updateMarketingPreferences,

      // Individual properties (spread subscriptions directly)
      ...subscriptions,

      // Derived properties
      ...derivedProperties,

      // Loading state (useful for showing loading indicators)
      loading,
    }),
    [
      registerForOnlineEvent,
      registerForExpoHallEvent,
      registerForARCEvent,
      registerForDiscoveryDayEvent,
      registerWithEasyRegistration,
      subscribeToMarketing,
      updateMarketingPreferences,
      subscriptions,
      derivedProperties,
      loading,
    ]
  )

  return <RegistrationContext.Provider value={value}>{children}</RegistrationContext.Provider>
}
